import {useState, Dispatch, SetStateAction} from 'react'
import {useParams, useLocation} from 'react-router-dom'
import {parseISO} from 'date-fns'

import {useGetRequest, useGetAML, RequestTagType} from 'modules/request'
import {useGetVariationRequest} from 'modules/request/api.variation'
import { useGetDefaultRequest } from 'modules/request/api.default'
import { useGetJournalRequest } from 'modules/request/api.journal'
import {SORT_ITEMS, DOCUMENTS_COLLECTION, IDocument, IDocumentsTab} from '.'

type UseDocumentsTabs = (
  tabs: IDocumentsTab[]
) => [IDocumentsTab, Dispatch<SetStateAction<IDocumentsTab>>]

type UseProjectUuid = (isRequest?: boolean, tag?: RequestTagType) => string

export const useDocumentsTabs: UseDocumentsTabs = (tabs) => {
  const {hash} = useLocation()
  const [activeTab, setActiveTab] = useState(
    (hash && tabs.find((tab) => tab.collection === hash.slice(1))) || tabs[0]
  )
  return [activeTab, setActiveTab]
}

type UseDocumentsFilter = (
  documents: IDocument[],
  collection?: string,
  isModelCollection?: boolean
) => {
  filteredDocuments: IDocument[]
  search: string
  sort: string
  setSearch: Dispatch<SetStateAction<string>>
  setSort: Dispatch<SetStateAction<string>>
}

type UseDrawdownsTabs = (
  documents: IDocument[],
  isRequest?: boolean
) => {
  drawdownsTabs?: IDocumentsTab[]
  drawdownsDocuments?: IDocument[]
}

export const useProjectUuid: UseProjectUuid = (isRequest = false, tag = "loan") => {
  const params = useParams()
  const {data: request} = useGetRequest(params.uuid, false)
  const {data: variationRequest} = useGetVariationRequest(params.uuid, false)
  const {data: defaultRequest} = useGetDefaultRequest(params.uuid, false)
  const {data: journalRequest} = useGetJournalRequest(params.uuid, false)
  const projectUuid = !isRequest ? params.uuid : 
                      (tag === 'variation') ? variationRequest.project.uuid :
                      (tag === 'default') ? defaultRequest.project.uuid :
                      (tag === 'journal') ? journalRequest.project.uuid :
                      request.project.uuid

  return projectUuid
}

export const useDocumentsFilter: UseDocumentsFilter = (
  documents,
  collection,
  isModelCollection
) => {
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState(SORT_ITEMS[0].key)

  let filteredDocuments = collection
    ? documents.filter(
        (document) =>
          (isModelCollection ? document.model.uuid : document.collection) ===
          collection
      )
    : documents.slice()

  if (search) {
    filteredDocuments = filteredDocuments.filter((document) =>
      document.name.toLowerCase().includes(search.toLowerCase())
    )
  }

  sortDocuments(filteredDocuments, sort)

  return {
    filteredDocuments,
    search,
    sort,
    setSearch,
    setSort,
  }
}

export const useAMLTabs = (projectUuid: string, documents?: IDocument[], disable?: boolean) => {
  const {data: aml} = useGetAML(projectUuid, !disable)
  if (!!disable) return {}

  const amlDocuments: IDocument[] = aml
    .map((item: any) =>
      [
        ...item.addressDocuments,
        ...item.identityDocuments,
        ...(item.other ?? []),
      ].map((document) => ({
        ...document,
        model: {
          uuid: item.uuid,
          type: 'amlCheck',
        },
      }))
    )
    .flat()

  const amlTabs: IDocumentsTab[] = [
    {
      collection: DOCUMENTS_COLLECTION.aml.key,
      label: DOCUMENTS_COLLECTION.aml.label,
      order: DOCUMENTS_COLLECTION.aml.categoryOrder,
      filesCount: documents
        ? documents.filter(
            (document) => document.collection === 'aml' && !document.archived
          ).length
        : undefined,
    },
    ...aml.map((item: any, index: number) => {
      const hasBorrowerDocument = [
        ...item.addressDocuments,
        ...item.identityDocuments,
      ].some((document) => document.createdBy?.role === 'user')
      return {
        hasBorrowerDocument,
        collection: item.uuid,
        label: item.name,
        order: index + 2,
        filesCount:
          item.addressDocuments.length +
          item.identityDocuments.length +
          (item.other
            ? item.other.filter((document: IDocument) => !document.archived)
                .length
            : 0),
      }
    }),
  ]

  return {amlTabs, amlDocuments}
}

export const useDrawdownsTabs: UseDrawdownsTabs = (documents, isRequest) => {
  if (isRequest) return {}

  const drawdownsDocuments = documents?.filter(
    (document) => document?.model?.type === 'drawdown'
  ) || []
  const drawdownsUuids = new Set(
    drawdownsDocuments.map((document) => document.model.uuid)
  )
  const drawdowns = Array.from(drawdownsUuids).map(
    (drawdownUuid) =>
      drawdownsDocuments.find(
        (document) => document.model.uuid === drawdownUuid
      ).model
  )
  drawdowns.sort((a, b) => a.sortOrder - b.sortOrder)
  const drawdownsTabs: IDocumentsTab[] = drawdowns.map((drawdown) => {
    return {
      collection: drawdown.uuid,
      label: drawdown.title,
      hasBorrowerDocument: false,
      order: drawdown.sortOrder,
      filesCount: drawdownsDocuments.filter(
        (document) =>
          document.model.uuid === drawdown.uuid && !document.archived
      ).length,
    }
  })

  return {drawdownsTabs, drawdownsDocuments}
}

const sortDocuments = (documents: IDocument[], sort: string) => {
  documents.sort((a, b) => {
    if (sort === 'date') {
      return +parseISO(b.createdAt) - +parseISO(a.createdAt)
    }
    if (sort === 'size') {
      return b.size - a.size
    }
    if (b.name > a.name) {
      return -1
    }
    if (b.name < a.name) {
      return 1
    }
    return 0
  })
}
